export type MultilingualLocale = "en" | "th" | "id" | "vn" | "fr" | "ru" | "pt" | "es" | "ko"

export const SUPPORTED_LOCALES: readonly MultilingualLocale[] = ["en", "th", "id", "vn", "fr", "ru", "pt", "es", "ko"]

const DEFAULT_LOCALE: MultilingualLocale = "en"

export interface BuildLocalePathOptions {
  preserveQuery?: boolean
  preserveHash?: boolean
  queryString?: string
  hashString?: string
  omitDefaultLocalePrefix?: boolean
}

export function normalizePathname(pathname: string): string {
  const withoutQuery = pathname.split("?")[0] || "/"
  const withoutHash = withoutQuery.split("#")[0] || "/"

  let normalized = withoutHash.startsWith("/") ? withoutHash : `/${withoutHash}`
  normalized = normalized.replace(/\/{2,}/g, "/")

  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }

  return normalized || "/"
}

export function isSupportedLocale(value?: string): value is MultilingualLocale {
  if (!value) {
    return false
  }

  return SUPPORTED_LOCALES.includes(value as MultilingualLocale)
}

export function getLocaleFromPath(pathname: string): MultilingualLocale | null {
  const normalizedPathname = normalizePathname(pathname)
  const [maybeLocale] = normalizedPathname.split("/").filter(Boolean)

  if (!isSupportedLocale(maybeLocale)) {
    return null
  }

  return maybeLocale
}

export function getPathSuffixWithoutLocale(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname)
  const segments = normalizedPathname.split("/").filter(Boolean)

  if (segments.length === 0) {
    return ""
  }

  const firstSegment = segments[0]
  const restSegments = isSupportedLocale(firstSegment) ? segments.slice(1) : segments

  return restSegments.length > 0 ? `/${restSegments.join("/")}` : ""
}

export function buildLocalePath(targetLocale: string, pathname: string, options: BuildLocalePathOptions = {}): string {
  const {
    preserveQuery = false,
    preserveHash = false,
    queryString = "",
    hashString = "",
    omitDefaultLocalePrefix = false,
  } = options
  const suffix = getPathSuffixWithoutLocale(pathname)
  const shouldOmitLocalePrefix = omitDefaultLocalePrefix && targetLocale === DEFAULT_LOCALE
  const newPath = shouldOmitLocalePrefix ? suffix || "" : suffix ? `/${targetLocale}${suffix}` : `/${targetLocale}`

  let fullPath = newPath
  const normalizedQueryString = queryString.startsWith("?") ? queryString.slice(1) : queryString
  const normalizedHashString = hashString.startsWith("#") ? hashString.slice(1) : hashString

  if (preserveQuery && normalizedQueryString) {
    fullPath += `?${normalizedQueryString}`
  }

  if (preserveHash && normalizedHashString) {
    fullPath += `#${normalizedHashString}`
  }

  return fullPath
}

export function getPathnameFromUrl(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}
